import React, {useCallback, useEffect, useState} from 'react';
import {CredentialResponse, GoogleLogin} from '@react-oauth/google';
import {useNavigate, Link, useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {loginStart, loginSuccess, loginFailure} from '../../store/slices/authSlice';
import {setUser} from '../../store/slices/userSlice';
import {authApi, AuthUser, InstagramAuthSession, LinkedInAuthSession, TikTokAuthSession, TwitterAuthSession} from '../../services/authApi';
import {enterpriseApi, EnterpriseInviteClaim} from '../../services/enterpriseApi';
import './Auth.css';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID?.trim() || '';

type ProviderIconProps = {
  className?: string;
};

const GoogleMark: React.FC<ProviderIconProps> = ({className}) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className={className}>
    <path
      d="M21.805 12.23c0-.69-.062-1.353-.177-1.99H12v3.764h5.502a4.704 4.704 0 0 1-2.04 3.087v2.565h3.3c1.932-1.779 3.043-4.4 3.043-7.426Z"
      fill="#4285F4"
    />
    <path
      d="M12 22.2c2.76 0 5.074-.915 6.765-2.544l-3.3-2.565c-.915.614-2.084.976-3.465.976-2.652 0-4.898-1.79-5.7-4.198H2.89v2.647A10.204 10.204 0 0 0 12 22.2Z"
      fill="#34A853"
    />
    <path
      d="M6.3 13.869A6.13 6.13 0 0 1 5.98 12c0-.648.111-1.278.32-1.869V7.484H2.89A10.199 10.199 0 0 0 1.8 12c0 1.633.392 3.18 1.09 4.516L6.3 13.869Z"
      fill="#FBBC04"
    />
    <path
      d="M12 5.933c1.5 0 2.847.516 3.908 1.53l2.931-2.93C17.069 2.88 14.756 1.8 12 1.8A10.204 10.204 0 0 0 2.89 7.484L6.3 10.13c.803-2.409 3.049-4.197 5.7-4.197Z"
      fill="#EA4335"
    />
  </svg>
);

const FacebookMark: React.FC<ProviderIconProps> = ({className}) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className={className}>
    <path
      d="M13.642 20v-6.378h2.139l.32-2.485h-2.459V9.55c0-.719.2-1.209 1.231-1.209h1.315V6.117c-.228-.03-1.01-.097-1.919-.097-1.898 0-3.198 1.159-3.198 3.287v1.83H8.922v2.485h2.149V20h2.571Z"
      fill="currentColor"
    />
  </svg>
);

const XMark: React.FC<ProviderIconProps> = ({className}) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className={className}>
    <path
      d="M18.244 3H21l-6.015 6.873L22 21h-5.487l-4.295-7.071L6.056 21H3.297l6.432-7.348L2 3h5.626l3.882 6.394L18.244 3Zm-.968 16.349h1.527L6.795 4.564H5.157l12.119 14.785Z"
      fill="currentColor"
    />
  </svg>
);

const LinkedInMark: React.FC<ProviderIconProps> = ({className}) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className={className}>
    <path
      d="M7.146 8.714a1.571 1.571 0 1 1 0-3.143a1.571 1.571 0 0 1 0 3.143Zm-1.35 1.2h2.7V18h-2.7V9.914Zm4.393 0h2.589v1.106h.037c.36-.683 1.24-1.403 2.553-1.403c2.73 0 3.235 1.797 3.235 4.134V18h-2.7v-3.792c0-.904-.016-2.066-1.259-2.066c-1.26 0-1.453.984-1.453 2v3.858h-2.702V9.914Z"
      fill="currentColor"
    />
  </svg>
);

const InstagramMark: React.FC<ProviderIconProps> = ({className}) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className={className}>
    <path
      d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm0 1.8A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95h-8.5Zm8.95 1.35a1.35 1.35 0 1 1 0 2.7a1.35 1.35 0 0 1 0-2.7ZM12 7.4A4.6 4.6 0 1 1 7.4 12A4.6 4.6 0 0 1 12 7.4Zm0 1.8A2.8 2.8 0 1 0 14.8 12A2.8 2.8 0 0 0 12 9.2Z"
      fill="currentColor"
    />
  </svg>
);

const TikTokMark: React.FC<ProviderIconProps> = ({className}) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    focusable="false"
    className={className}>
    <path
      d="M18.865 7.42a5.364 5.364 0 0 1-3.495-1.293V14.34a4.837 4.837 0 1 1-4.836-4.836c.31 0 .617.03.919.09v2.487a2.424 2.424 0 1 0 1.504 2.259V2.5h2.413c.17 1.45 1.023 2.73 2.297 3.481c.37.218.778.375 1.198.47v.97Z"
      fill="#25F4EE"
      opacity="0.7"
      transform="translate(-0.35 0.25)"
    />
    <path
      d="M18.865 7.42a5.364 5.364 0 0 1-3.495-1.293V14.34a4.837 4.837 0 1 1-4.836-4.836c.31 0 .617.03.919.09v2.487a2.424 2.424 0 1 0 1.504 2.259V2.5h2.413c.17 1.45 1.023 2.73 2.297 3.481c.37.218.778.375 1.198.47v.97Z"
      fill="#FE2C55"
      opacity="0.75"
      transform="translate(0.35 -0.25)"
    />
    <path
      d="M18.865 7.42a5.364 5.364 0 0 1-3.495-1.293V14.34a4.837 4.837 0 1 1-4.836-4.836c.31 0 .617.03.919.09v2.487a2.424 2.424 0 1 0 1.504 2.259V2.5h2.413c.17 1.45 1.023 2.73 2.297 3.481c.37.218.778.375 1.198.47v.97Z"
      fill="currentColor"
    />
  </svg>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteClaim, setInviteClaim] = useState<EnterpriseInviteClaim | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [facebookError, setFacebookError] = useState<string | null>(null);
  const [, setInstagramSession] = useState<InstagramAuthSession | null>(null);
  const [instagramError, setInstagramError] = useState<string | null>(null);
  const [twitterSession, setTwitterSession] = useState<TwitterAuthSession | null>(null);
  const [twitterEmail, setTwitterEmail] = useState('');
  const [twitterCode, setTwitterCode] = useState('');
  const [twitterEmailSent, setTwitterEmailSent] = useState(false);
  const [twitterError, setTwitterError] = useState<string | null>(null);
  const [twitterStatusMessage, setTwitterStatusMessage] = useState<string | null>(null);
  const [twitterEmailLoading, setTwitterEmailLoading] = useState(false);
  const [linkedinSession, setLinkedinSession] = useState<LinkedInAuthSession | null>(null);
  const [linkedinEmail, setLinkedinEmail] = useState('');
  const [linkedinCode, setLinkedinCode] = useState('');
  const [linkedinEmailSent, setLinkedinEmailSent] = useState(false);
  const [linkedinError, setLinkedinError] = useState<string | null>(null);
  const [linkedinStatusMessage, setLinkedinStatusMessage] = useState<string | null>(null);
  const [linkedinEmailLoading, setLinkedinEmailLoading] = useState(false);
  const [, setTiktokSession] = useState<TikTokAuthSession | null>(null);
  const [tiktokError, setTiktokError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const inviteToken = new URLSearchParams(location.search).get('enterpriseInvite')?.trim() || '';

  useEffect(() => {
    if (!inviteToken) {
      setInviteClaim(null);
      setInviteError(null);
      setInviteLoading(false);
      return;
    }

    let isMounted = true;
    setInviteLoading(true);
    setInviteError(null);

    enterpriseApi.claimInvite(inviteToken)
      .then((claim) => {
        if (!isMounted) {
          return;
        }

        setInviteClaim(claim);
        setEmail(claim.email);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        setInviteClaim(null);
        setInviteError(error instanceof Error ? error.message : 'Failed to validate Enterprise invite.');
      })
      .finally(() => {
        if (isMounted) {
          setInviteLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [inviteToken]);

  const completeLogin = useCallback(async (token: string, user: AuthUser) => {
    dispatch(loginSuccess(token));
    dispatch(setUser(user));

    if (inviteToken) {
      if (!user.email?.trim()) {
        setLoading(false);
        alert('Enterprise invites still require an account with an email address. Sign in succeeded, but this invite was not accepted.');
        navigate('/home');
        return;
      }

      try {
        await enterpriseApi.acceptInvite(token, inviteToken);
        const refreshedUser = await authApi.getCurrentUser(token);
        dispatch(setUser(refreshedUser));
        setLoading(false);
        navigate('/settings?enterprise=joined');
        return;
      } catch (acceptError) {
        const refreshedUser = await authApi.getCurrentUser(token).catch(() => user);
        dispatch(setUser(refreshedUser));
        setLoading(false);
        alert(authApi.getErrorMessage(acceptError));
        navigate('/settings');
        return;
      }
    }

    setLoading(false);
    navigate('/home');
  }, [dispatch, inviteToken, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const facebookAuthState = searchParams.get('facebookAuthState')?.trim() || '';
    const facebookAuthResult = searchParams.get('facebookAuthResult')?.trim() || '';
    const facebookAuthMessage = searchParams.get('facebookAuthMessage')?.trim() || '';

    if (!facebookAuthState) {
      if (facebookAuthResult === 'failed') {
        const message = facebookAuthMessage || 'Facebook sign-in could not be completed';
        setFacebookError(message);
        dispatch(loginFailure(message));
      }
      return;
    }

    if (facebookAuthResult === 'failed') {
      const message = facebookAuthMessage || 'Facebook sign-in could not be completed';
      setFacebookError(message);
      dispatch(loginFailure(message));
      return;
    }

    let isMounted = true;
    setLoading(true);
    setFacebookError(null);
    dispatch(loginStart());

    authApi.getFacebookLoginSession(facebookAuthState)
      .then(async () => {
        if (!isMounted) {
          return;
        }

        const response = await authApi.completeFacebookLogin({state: facebookAuthState});

        if (!isMounted) {
          return;
        }

        await completeLogin(response.token, response.user);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        const message = authApi.getErrorMessage(error);
        setFacebookError(message);
        dispatch(loginFailure(message));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [completeLogin, dispatch, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const twitterAuthState = searchParams.get('twitterAuthState')?.trim() || '';
    const twitterAuthResult = searchParams.get('twitterAuthResult')?.trim() || '';
    const twitterAuthMessage = searchParams.get('twitterAuthMessage')?.trim() || '';

    if (!twitterAuthState) {
      if (twitterAuthResult === 'failed') {
        const message = twitterAuthMessage || 'X sign-in could not be completed';
        setTwitterError(message);
        dispatch(loginFailure(message));
      }
      return;
    }

    if (twitterAuthResult === 'failed') {
      const message = twitterAuthMessage || 'X sign-in could not be completed';
      setTwitterSession(null);
      setTwitterError(message);
      dispatch(loginFailure(message));
      return;
    }

    let isMounted = true;
    setLoading(true);
    setTwitterError(null);
    setTwitterStatusMessage(null);
    dispatch(loginStart());

    authApi.getTwitterLoginSession(twitterAuthState)
      .then(async (nextSession) => {
        if (!isMounted) {
          return;
        }

        setTwitterSession(nextSession);
        setTwitterEmail(nextSession.email || inviteClaim?.email || '');
        setTwitterCode('');
        setTwitterEmailSent(false);

        if (!nextSession.requiresEmail) {
          const response = await authApi.completeTwitterLogin({state: twitterAuthState});

          if (!isMounted) {
            return;
          }

          await completeLogin(response.token, response.user);
          return;
        }

        setLoading(false);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        const message = authApi.getErrorMessage(error);
        setTwitterSession(null);
        setTwitterError(message);
        dispatch(loginFailure(message));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [completeLogin, dispatch, inviteClaim?.email, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tiktokAuthState = searchParams.get('tiktokAuthState')?.trim() || '';
    const tiktokAuthResult = searchParams.get('tiktokAuthResult')?.trim() || '';
    const tiktokAuthMessage = searchParams.get('tiktokAuthMessage')?.trim() || '';

    if (!tiktokAuthState) {
      if (tiktokAuthResult === 'failed') {
        const message = tiktokAuthMessage || 'TikTok sign-in could not be completed';
        setTiktokError(message);
        dispatch(loginFailure(message));
      }
      return;
    }

    if (tiktokAuthResult === 'failed') {
      const message = tiktokAuthMessage || 'TikTok sign-in could not be completed';
      setTiktokSession(null);
      setTiktokError(message);
      dispatch(loginFailure(message));
      return;
    }

    let isMounted = true;
    setLoading(true);
    setTiktokError(null);
    dispatch(loginStart());

    authApi.getTikTokLoginSession(tiktokAuthState)
      .then(async (nextSession) => {
        if (!isMounted) {
          return;
        }

        setTiktokSession(nextSession);

        if (!nextSession.requiresEmail) {
          const response = await authApi.completeTikTokLogin({state: tiktokAuthState});

          if (!isMounted) {
            return;
          }

          await completeLogin(response.token, response.user);
          return;
        }

        setLoading(false);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        const message = authApi.getErrorMessage(error);
        setTiktokSession(null);
        setTiktokError(message);
        dispatch(loginFailure(message));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [completeLogin, dispatch, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const linkedinAuthState = searchParams.get('linkedinAuthState')?.trim() || '';
    const linkedinAuthResult = searchParams.get('linkedinAuthResult')?.trim() || '';
    const linkedinAuthMessage = searchParams.get('linkedinAuthMessage')?.trim() || '';

    if (!linkedinAuthState) {
      if (linkedinAuthResult === 'failed') {
        const message = linkedinAuthMessage || 'LinkedIn sign-in could not be completed';
        setLinkedinError(message);
        dispatch(loginFailure(message));
      }
      return;
    }

    if (linkedinAuthResult === 'failed') {
      const message = linkedinAuthMessage || 'LinkedIn sign-in could not be completed';
      setLinkedinSession(null);
      setLinkedinError(message);
      dispatch(loginFailure(message));
      return;
    }

    let isMounted = true;
    setLoading(true);
    setLinkedinError(null);
    setLinkedinStatusMessage(null);
    dispatch(loginStart());

    authApi.getLinkedInLoginSession(linkedinAuthState)
      .then(async (nextSession) => {
        if (!isMounted) {
          return;
        }

        setLinkedinSession(nextSession);
        setLinkedinEmail(nextSession.email || inviteClaim?.email || '');
        setLinkedinCode('');
        setLinkedinEmailSent(false);

        if (!nextSession.requiresEmail) {
          const response = await authApi.completeLinkedInLogin({state: linkedinAuthState});

          if (!isMounted) {
            return;
          }

          await completeLogin(response.token, response.user);
          return;
        }

        setLoading(false);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        const message = authApi.getErrorMessage(error);
        setLinkedinSession(null);
        setLinkedinError(message);
        dispatch(loginFailure(message));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [completeLogin, dispatch, inviteClaim?.email, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const instagramAuthState = searchParams.get('instagramAuthState')?.trim() || '';
    const instagramAuthResult = searchParams.get('instagramAuthResult')?.trim() || '';
    const instagramAuthMessage = searchParams.get('instagramAuthMessage')?.trim() || '';

    if (!instagramAuthState) {
      if (instagramAuthResult === 'failed') {
        const message = instagramAuthMessage || 'Instagram sign-in could not be completed';
        setInstagramError(message);
        dispatch(loginFailure(message));
      }
      return;
    }

    if (instagramAuthResult === 'failed') {
      const message = instagramAuthMessage || 'Instagram sign-in could not be completed';
      setInstagramSession(null);
      setInstagramError(message);
      dispatch(loginFailure(message));
      return;
    }

    let isMounted = true;
    setLoading(true);
    setInstagramError(null);
    dispatch(loginStart());

    authApi.getInstagramLoginSession(instagramAuthState)
      .then(async (nextSession) => {
        if (!isMounted) {
          return;
        }

        setInstagramSession(nextSession);

        if (!nextSession.requiresEmail) {
          const response = await authApi.completeInstagramLogin({state: instagramAuthState});

          if (!isMounted) {
            return;
          }

          await completeLogin(response.token, response.user);
          return;
        }

        setLoading(false);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }

        const message = authApi.getErrorMessage(error);
        setInstagramSession(null);
        setInstagramError(message);
        dispatch(loginFailure(message));
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [completeLogin, dispatch, inviteClaim?.email, location.search]);

  useEffect(() => {
    if (inviteClaim?.email && twitterSession?.requiresEmail) {
      setTwitterEmail(inviteClaim.email);
    }
  }, [twitterSession?.requiresEmail, inviteClaim?.email]);

  useEffect(() => {
    if (inviteClaim?.email && linkedinSession?.requiresEmail) {
      setLinkedinEmail(inviteClaim.email);
    }
  }, [linkedinSession?.requiresEmail, inviteClaim?.email]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      alert('Please fill in all fields');
      return;
    }

    setLoading(true);
    dispatch(loginStart());

    try {
      const response = await authApi.login(email, password);
      await completeLogin(response.token, response.user);
    } catch (error: any) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLoading(false);
      alert(message);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      const message = 'Google sign-in did not return a credential';
      dispatch(loginFailure(message));
      alert(message);
      return;
    }

    setLoading(true);
    dispatch(loginStart());

    try {
      const response = await authApi.loginWithGoogle(credentialResponse.credential);
      await completeLogin(response.token, response.user);
    } catch (error: any) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLoading(false);
      alert(message);
    }
  };

  const handleGoogleError = () => {
    const message = 'Google sign-in could not be completed';
    dispatch(loginFailure(message));
    alert(message);
  };

  const handleFacebookLogin = async () => {
    setLoading(true);
    dispatch(loginStart());
    setFacebookError(null);

    try {
      const returnPath = `${location.pathname}${location.search}`;
      const response = await authApi.startFacebookLogin(returnPath);
      window.location.assign(response.authorizeUrl);
    } catch (error: any) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setFacebookError(message);
      setLoading(false);
      alert(message);
    }
  };

  const handleTwitterLogin = async () => {
    setLoading(true);
    dispatch(loginStart());
    setTwitterError(null);
    setTwitterStatusMessage(null);

    try {
      const returnPath = `${location.pathname}${location.search}`;
      const response = await authApi.startTwitterLogin(returnPath);
      window.location.assign(response.authorizeUrl);
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLoading(false);
      alert(message);
    }
  };

  const handleTwitterEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!twitterSession) {
      return;
    }

    if (!twitterEmail.trim()) {
      setTwitterError('Please enter your email address');
      return;
    }

    setTwitterEmailLoading(true);
    setTwitterError(null);
    setTwitterStatusMessage(null);

    try {
      await authApi.sendTwitterLoginEmailCode(twitterSession.state, twitterEmail.trim());
      setTwitterEmailSent(true);
      setTwitterStatusMessage('Check your email for a verification code.');
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      setTwitterError(message);
      setLoading(false);
    } finally {
      setTwitterEmailLoading(false);
    }
  };

  const handleTwitterComplete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!twitterSession) {
      return;
    }

    if (!twitterEmail.trim() || !twitterCode.trim()) {
      setTwitterError('Enter your email and verification code to continue.');
      return;
    }

    setLoading(true);
    setTwitterError(null);
    dispatch(loginStart());

    try {
      const response = await authApi.completeTwitterLogin({
        state: twitterSession.state,
        email: twitterEmail.trim(),
        code: twitterCode.trim(),
      });
      await completeLogin(response.token, response.user);
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setTwitterError(message);
      setLoading(false);
    }
  };

  const handleCancelTwitterLogin = () => {
    setTwitterSession(null);
    setTwitterEmail('');
    setTwitterCode('');
    setTwitterEmailSent(false);
    setTwitterError(null);
    setTwitterStatusMessage(null);
    navigate(inviteToken ? `/login?enterpriseInvite=${inviteToken}` : '/login', {replace: true});
  };

  const handleLinkedInLogin = async () => {
    setLoading(true);
    dispatch(loginStart());
    setLinkedinError(null);
    setLinkedinStatusMessage(null);

    try {
      const returnPath = `${location.pathname}${location.search}`;
      const response = await authApi.startLinkedInLogin(returnPath);
      window.location.assign(response.authorizeUrl);
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLoading(false);
      alert(message);
    }
  };

  const handleLinkedInEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!linkedinSession) {
      return;
    }

    if (!linkedinEmail.trim()) {
      setLinkedinError('Please enter your email address');
      return;
    }

    setLinkedinEmailLoading(true);
    setLinkedinError(null);
    setLinkedinStatusMessage(null);

    try {
      await authApi.sendLinkedInLoginEmailCode(linkedinSession.state, linkedinEmail.trim());
      setLinkedinEmailSent(true);
      setLinkedinStatusMessage('Check your email for a verification code.');
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      setLinkedinError(message);
      setLoading(false);
    } finally {
      setLinkedinEmailLoading(false);
    }
  };

  const handleLinkedInComplete = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!linkedinSession) {
      return;
    }

    if (!linkedinEmail.trim() || !linkedinCode.trim()) {
      setLinkedinError('Enter your email and verification code to continue.');
      return;
    }

    setLoading(true);
    setLinkedinError(null);
    dispatch(loginStart());

    try {
      const response = await authApi.completeLinkedInLogin({
        state: linkedinSession.state,
        email: linkedinEmail.trim(),
        code: linkedinCode.trim(),
      });
      await completeLogin(response.token, response.user);
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLinkedinError(message);
      setLoading(false);
    }
  };

  const handleCancelLinkedInLogin = () => {
    setLinkedinSession(null);
    setLinkedinEmail('');
    setLinkedinCode('');
    setLinkedinEmailSent(false);
    setLinkedinError(null);
    setLinkedinStatusMessage(null);
    navigate(inviteToken ? `/login?enterpriseInvite=${inviteToken}` : '/login', {replace: true});
  };

  const handleTikTokLogin = async () => {
    setLoading(true);
    dispatch(loginStart());
    setTiktokError(null);

    try {
      const returnPath = `${location.pathname}${location.search}`;
      const response = await authApi.startTikTokLogin(returnPath);
      window.location.assign(response.authorizeUrl);
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLoading(false);
      alert(message);
    }
  };

  const handleInstagramLogin = async () => {
    setLoading(true);
    dispatch(loginStart());
    setInstagramError(null);

    try {
      const returnPath = `${location.pathname}${location.search}`;
      const response = await authApi.startInstagramLogin(returnPath);
      window.location.assign(response.authorizeUrl);
    } catch (error) {
      const message = authApi.getErrorMessage(error);
      dispatch(loginFailure(message));
      setLoading(false);
      alert(message);
    }
  };
  const twitterProfileLabel = twitterSession?.profile.displayName
    || twitterSession?.profile.username
    || 'X account';
  const showTwitterEmailStep = Boolean(
    twitterSession && twitterSession.requiresEmail && !twitterSession.linked
  );
  const linkedinProfileLabel = linkedinSession?.profile.displayName || 'LinkedIn account';
  const showLinkedInEmailStep = Boolean(
    linkedinSession && linkedinSession.requiresEmail && !linkedinSession.linked
  );
  return (
    <div className="auth-container">
      <div className="auth-page-content">
        <div className="auth-card">
        <h1 className="auth-title">Welcome to XPSR</h1>
        <p className="auth-subtitle">
          {inviteClaim
            ? `Sign in to join ${inviteClaim.organizationName}`
            : 'Sign in to continue'}
        </p>
        <p className="auth-trust-note">We send verification codes only when users request account access.</p>

        {inviteLoading ? (
          <div className="auth-invite-banner">Checking your Enterprise invite…</div>
        ) : null}

        {inviteClaim ? (
          <div className="auth-invite-banner auth-invite-banner-success">
            <strong>{inviteClaim.organizationName}</strong> invited you as a <strong>{inviteClaim.role}</strong>.
          </div>
        ) : null}

        {inviteError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{inviteError}</div>
        ) : null}

        {facebookError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{facebookError}</div>
        ) : null}

        {instagramError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{instagramError}</div>
        ) : null}

        {twitterError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{twitterError}</div>
        ) : null}

        {linkedinError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{linkedinError}</div>
        ) : null}

        {tiktokError ? (
          <div className="auth-invite-banner auth-invite-banner-error">{tiktokError}</div>
        ) : null}

        {showTwitterEmailStep ? (
          <div className="auth-twitter-panel">
            <div className="auth-twitter-profile">
              {twitterSession?.profile.profileImageUrl ? (
                <img
                  src={twitterSession.profile.profileImageUrl}
                  alt=""
                  className="auth-twitter-avatar"
                />
              ) : (
                <span className="auth-twitter-avatar auth-twitter-avatar-fallback">X</span>
              )}

              <div>
                <strong>{twitterProfileLabel}</strong>
                {twitterSession?.profile.username ? (
                  <span>@{twitterSession.profile.username}</span>
                ) : null}
              </div>
            </div>

            <form onSubmit={handleTwitterEmailSubmit} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                value={twitterEmail}
                onChange={(e) => setTwitterEmail(e.target.value)}
                className="input"
                disabled={Boolean(inviteClaim) || twitterEmailLoading || loading}
                required
              />

              <button
                type="submit"
                className="button button-primary auth-button"
                disabled={twitterEmailLoading || loading}>
                {twitterEmailLoading
                  ? 'Sending code...'
                  : twitterEmailSent ? 'Send New Code' : 'Send Verification Code'}
              </button>
            </form>

            {twitterStatusMessage ? (
              <div className="auth-invite-banner auth-invite-banner-success">{twitterStatusMessage}</div>
            ) : null}

            <form onSubmit={handleTwitterComplete} className="auth-form">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Verification code"
                value={twitterCode}
                onChange={(e) => setTwitterCode(e.target.value)}
                className="input"
                disabled={!twitterEmailSent || loading}
                required
              />

              <button
                type="submit"
                className="button button-primary auth-button"
                disabled={!twitterEmailSent || loading}>
                {loading ? 'Signing in...' : 'Continue with X'}
              </button>
            </form>

            <button
              type="button"
              className="auth-secondary-action"
              onClick={handleCancelTwitterLogin}
              disabled={loading || twitterEmailLoading}>
              Use a different sign-in method
            </button>
          </div>
        ) : showLinkedInEmailStep ? (
          <div className="auth-linkedin-panel">
            <div className="auth-linkedin-profile">
              {linkedinSession?.profile.profileImageUrl ? (
                <img
                  src={linkedinSession.profile.profileImageUrl}
                  alt=""
                  className="auth-linkedin-avatar"
                />
              ) : (
                <span className="auth-linkedin-avatar auth-linkedin-avatar-fallback">in</span>
              )}

              <div>
                <strong>{linkedinProfileLabel}</strong>
                <span>LinkedIn account</span>
              </div>
            </div>

            <form onSubmit={handleLinkedInEmailSubmit} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                value={linkedinEmail}
                onChange={(e) => setLinkedinEmail(e.target.value)}
                className="input"
                disabled={Boolean(inviteClaim) || linkedinEmailLoading || loading}
                required
              />

              <button
                type="submit"
                className="button button-primary auth-button"
                disabled={linkedinEmailLoading || loading}>
                {linkedinEmailLoading
                  ? 'Sending code...'
                  : linkedinEmailSent ? 'Send New Code' : 'Send Verification Code'}
              </button>
            </form>

            {linkedinStatusMessage ? (
              <div className="auth-invite-banner auth-invite-banner-success">{linkedinStatusMessage}</div>
            ) : null}

            <form onSubmit={handleLinkedInComplete} className="auth-form">
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Verification code"
                value={linkedinCode}
                onChange={(e) => setLinkedinCode(e.target.value)}
                className="input"
                disabled={!linkedinEmailSent || loading}
                required
              />

              <button
                type="submit"
                className="button button-primary auth-button"
                disabled={!linkedinEmailSent || loading}>
                {loading ? 'Signing in...' : 'Continue with LinkedIn'}
              </button>
            </form>

            <button
              type="button"
              className="auth-secondary-action"
              onClick={handleCancelLinkedInLogin}
              disabled={loading || linkedinEmailLoading}>
              Use a different sign-in method
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                disabled={Boolean(inviteClaim)}
                required
              />

              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                required
              />

              <button
                type="submit"
                className="button button-primary auth-button"
                disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-divider">
              <span>or</span>
            </div>

            <div className="auth-provider-stack">
              {GOOGLE_CLIENT_ID ? (
                <div className="auth-google-button">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    theme="outline"
                    shape="pill"
                    text="continue_with"
                    width="320"
                  />
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="auth-google-placeholder"
                    disabled
                    aria-disabled="true">
                    <span className="auth-google-placeholder-icon">
                      <GoogleMark className="auth-provider-icon-svg auth-provider-icon-svg-google" />
                    </span>
                    <span>Continue with Google</span>
                  </button>

                  <p className="auth-provider-note">
                    Set <code>REACT_APP_GOOGLE_CLIENT_ID</code> to enable Google sign-in.
                  </p>
                </>
              )}

              <button
                type="button"
                className="auth-facebook-button"
                disabled={loading}
                onClick={handleFacebookLogin}>
                <span className="auth-facebook-button-icon">
                  <FacebookMark className="auth-provider-icon-svg auth-provider-icon-svg-facebook" />
                </span>
                <span>Continue with Facebook</span>
              </button>

              <button
                type="button"
                className="auth-twitter-button"
                disabled={loading}
                onClick={handleTwitterLogin}>
                <span className="auth-twitter-button-icon">
                  <XMark className="auth-provider-icon-svg auth-provider-icon-svg-x" />
                </span>
                <span>Continue with X</span>
              </button>

              <button
                type="button"
                className="auth-linkedin-button"
                disabled={loading}
                onClick={handleLinkedInLogin}>
                <span className="auth-linkedin-button-icon">
                  <LinkedInMark className="auth-provider-icon-svg auth-provider-icon-svg-linkedin" />
                </span>
                <span>Continue with LinkedIn</span>
              </button>

              <button
                type="button"
                className="auth-tiktok-button"
                disabled={loading}
                onClick={handleTikTokLogin}>
                <span className="auth-tiktok-button-icon">
                  <TikTokMark className="auth-provider-icon-svg auth-provider-icon-svg-tiktok" />
                </span>
                <span>Continue with TikTok</span>
              </button>

              <button
                type="button"
                className="auth-instagram-button"
                disabled={loading}
                onClick={handleInstagramLogin}>
                <span className="auth-instagram-button-icon">
                  <InstagramMark className="auth-provider-icon-svg auth-provider-icon-svg-instagram" />
                </span>
                <span>Continue with Instagram</span>
              </button>
            </div>
          </>
        )}
        
        <p className="auth-link">
          Don't have an account? <Link to={inviteToken ? `/register?enterpriseInvite=${inviteToken}` : '/register'}>Sign Up</Link>
        </p>
        </div>

        <footer className="auth-public-footer">
          <nav className="auth-public-links" aria-label="Public pages">
            <Link to="/about">About</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/contact">Contact Us</Link>
          </nav>
          <p>&copy; 2026 XPSR. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default Login;
